import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RichTextEditor } from '@/components/learn/admin/RichTextEditor';
import { PdfUpload } from '@/components/learn/admin/PdfUpload';
import { Upload, X, Trash2 } from 'lucide-react';
import { useEventAssociationLinks } from '@/hooks/useEventAssociationLinks';
import { useEventAssociationTypes } from '@/hooks/useEventAssociationTypes';
import { useEventsAssociations } from '@/hooks/useEventsAssociations';
import type { Event } from '@/types/events';

const formSchema = z.object({
  recording_link: z.string().optional(),
  invitation_recording_link: z.string().optional(),
  what_you_will_learn: z.string().optional(),
  story_behind: z.string().optional(),
  for_whom: z.string().optional(),
  long_description: z.string().optional(),
  benefits: z.string().optional(),
  goals: z.string().optional(),
  visual_1: z.string().optional(),
  visual_2: z.string().optional(),
  syllabus: z.string().optional(),
});

type FormData = z.infer<typeof formSchema>;

interface EventAdditionalInfoProps {
  event: Event;
  brandId: string;
}

export function EventAdditionalInfo({ event, brandId }: EventAdditionalInfoProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadingVisual, setUploadingVisual] = useState<1 | 2 | null>(null);
  const [defaultTypeId, setDefaultTypeId] = useState<string>('');
  const { links, associationIds, isLoading: linksLoading, setLinksMutation } = useEventAssociationLinks(event.id);
  const { associations = [], isLoading: associationsLoading } = useEventsAssociations(brandId);
  const { types: associationTypes = [], isLoading: typesLoading } = useEventAssociationTypes();
  const effectiveDefaultTypeId = defaultTypeId || associationTypes[0]?.id || '';

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      recording_link: event.recording_link || '',
      invitation_recording_link: event.invitation_recording_link || '',
      what_you_will_learn: event.what_you_will_learn || '',
      story_behind: event.story_behind || '',
      for_whom: event.for_whom || '',
      long_description: event.long_description || '',
      benefits: event.benefits || '',
      goals: event.goals || '',
      visual_1: event.visual_1 || '',
      visual_2: event.visual_2 || '',
      syllabus: event.syllabus || '',
    },
  });

  const currentLinkRows = links.map((l) => ({ association_id: l.association_id, association_type_id: l.association_type_id }));

  const handleAddAssociation = (associationId: string, associationTypeId: string) => {
    if (!associationId || associationIds.includes(associationId)) return;
    if (!associationTypeId) return;
    setLinksMutation.mutate({
      eventId: event.id,
      links: [...currentLinkRows, { association_id: associationId, association_type_id: associationTypeId }],
    });
  };

  const handleRemoveAssociation = (associationId: string) => {
    setLinksMutation.mutate({
      eventId: event.id,
      links: currentLinkRows.filter((r) => r.association_id !== associationId),
    });
  };

  const handleChangeLinkType = (associationId: string, associationTypeId: string) => {
    setLinksMutation.mutate({
      eventId: event.id,
      links: currentLinkRows.map((r) =>
        r.association_id === associationId ? { ...r, association_type_id: associationTypeId } : r
      ),
    });
  };

  const handleImageUpload = async (file: File, visualNumber: 1 | 2) => {
    try {
      setUploadingVisual(visualNumber);
      const fileExt = file.name.split('.').pop();
      const fileName = `event/${brandId}/${event.id}/visual_${visualNumber}_${Date.now()}.${fileExt}`;
      const { error: uploadError } = await supabase.storage
        .from('course-thumbnails')
        .upload(fileName, file, { cacheControl: '3600', upsert: true });
      if (uploadError) throw uploadError;
      const { data: { publicUrl } } = supabase.storage.from('course-thumbnails').getPublicUrl(fileName);
      form.setValue(`visual_${visualNumber}`, publicUrl);
      toast.success(`Visual ${visualNumber} uploaded`);
    } catch (err: unknown) {
      toast.error(`Failed to upload: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setUploadingVisual(null);
    }
  };

  const onSubmit = async (data: FormData) => {
    try {
      setIsSubmitting(true);
      const { error } = await supabase
        .from('os_events')
        .update({
          recording_link: data.recording_link || null,
          invitation_recording_link: data.invitation_recording_link || null,
          what_you_will_learn: data.what_you_will_learn || null,
          story_behind: data.story_behind || null,
          for_whom: data.for_whom || null,
          long_description: data.long_description || null,
          benefits: data.benefits || null,
          goals: data.goals || null,
          visual_1: data.visual_1 || null,
          visual_2: data.visual_2 || null,
          syllabus: data.syllabus || null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', event.id);
      if (error) throw error;
      toast.success('Additional information updated');
    } catch (err: unknown) {
      toast.error(`Failed to update: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderVisualUpload = (visualNumber: 1 | 2) => {
    const fieldName = `visual_${visualNumber}` as const;
    const currentValue = form.watch(fieldName);
    return (
      <div className="space-y-2">
        <FormLabel>Visual {visualNumber}</FormLabel>
        {currentValue ? (
          <div className="relative">
            <img src={currentValue} alt={`Visual ${visualNumber}`} className="w-full h-48 object-cover rounded-lg border" />
            <Button type="button" variant="destructive" size="icon" className="absolute top-2 right-2" onClick={() => form.setValue(fieldName, '')}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <div className="border-2 border-dashed rounded-lg p-6 text-center">
            <Input
              type="file"
              accept="image/*"
              className="hidden"
              id={`event-visual-${visualNumber}`}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleImageUpload(file, visualNumber);
              }}
              disabled={uploadingVisual === visualNumber}
            />
            <label htmlFor={`event-visual-${visualNumber}`} className="cursor-pointer flex flex-col items-center gap-2">
              <Upload className="h-8 w-8 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">{uploadingVisual === visualNumber ? 'Uploading...' : 'Click to upload'}</span>
            </label>
          </div>
        )}
        <FormField
          control={form.control}
          name={fieldName}
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Input {...field} placeholder="Or paste image URL" disabled={uploadingVisual === visualNumber} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Additional Event Information</CardTitle>
        <CardDescription>Extended details, visuals, and marketing content for your event</CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div className="border-b pb-6">
              <h3 className="text-lg font-semibold mb-4">Associations</h3>
              <p className="text-sm text-muted-foreground mb-3">Link this event to one or more associations (e.g. sponsors, partners) and set the type for each.</p>
              {linksLoading || associationsLoading || typesLoading ? (
                <p className="text-sm text-muted-foreground">Loading...</p>
              ) : (
                <div className="space-y-3">
                  {links.length > 0 && (
                    <ul className="space-y-2">
                      {links.map((link) => (
                        <li
                          key={link.id}
                          className="flex items-center justify-between gap-2 rounded-md border bg-muted/30 px-3 py-2"
                        >
                          <div className="flex items-center gap-2 min-w-0 flex-1">
                            {link.association?.logo_url ? (
                              <img
                                src={link.association.logo_url}
                                alt=""
                                className="h-8 w-8 rounded object-cover shrink-0"
                              />
                            ) : null}
                            <span className="font-medium truncate">{link.association?.name ?? link.association_id}</span>
                            <Select
                              value={link.association_type_id}
                              onValueChange={(value) => handleChangeLinkType(link.association_id, value)}
                              disabled={setLinksMutation.isPending}
                            >
                              <SelectTrigger className="w-[120px] shrink-0 h-8">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {associationTypes.map((t) => (
                                  <SelectItem key={t.id} value={t.id}>
                                    {t.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="shrink-0 text-destructive hover:text-destructive"
                            onClick={() => handleRemoveAssociation(link.association_id)}
                            disabled={setLinksMutation.isPending}
                            title="Remove association"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className="flex flex-wrap gap-2 items-center">
                    <Select
                      value=""
                      onValueChange={(value) => {
                        if (value && effectiveDefaultTypeId) handleAddAssociation(value, effectiveDefaultTypeId);
                      }}
                    >
                      <SelectTrigger className="w-[220px]" disabled={associations.filter((a) => !associationIds.includes(a.id)).length === 0}>
                        <SelectValue placeholder="Add association..." />
                      </SelectTrigger>
                      <SelectContent>
                        {associations
                          .filter((a) => !associationIds.includes(a.id))
                          .map((a) => (
                            <SelectItem key={a.id} value={a.id}>
                              {a.name}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                    <Select
                      value={effectiveDefaultTypeId}
                      onValueChange={setDefaultTypeId}
                    >
                      <SelectTrigger className="w-[130px]" disabled={associationTypes.length === 0}>
                        <SelectValue placeholder="Type" />
                      </SelectTrigger>
                      <SelectContent>
                        {associationTypes.map((t) => (
                          <SelectItem key={t.id} value={t.id}>
                            {t.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <span className="text-xs text-muted-foreground">as type for new</span>
                  </div>
                </div>
              )}
            </div>
            <div className="border-b pb-6">
              <h3 className="text-lg font-semibold mb-4">Details & Links</h3>
              <div className="space-y-4">
                <FormField
                  control={form.control}
                  name="recording_link"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Recording link</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="https://..." />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="invitation_recording_link"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Invitation recording link</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="https://..." />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="what_you_will_learn"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>What you will learn</FormLabel>
                      <FormControl>
                        <RichTextEditor content={field.value || ''} onChange={field.onChange} placeholder="What attendees will learn..." />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="story_behind"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Story behind</FormLabel>
                      <FormControl>
                        <RichTextEditor content={field.value || ''} onChange={field.onChange} placeholder="The story behind this event..." />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
            <FormField
              control={form.control}
              name="for_whom"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Who is this event for?</FormLabel>
                  <FormControl>
                    <RichTextEditor content={field.value || ''} onChange={field.onChange} placeholder="Describe your target audience..." />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="long_description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Long Description</FormLabel>
                  <FormControl>
                    <RichTextEditor content={field.value || ''} onChange={field.onChange} placeholder="Detailed description..." />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="benefits"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Benefits</FormLabel>
                  <FormControl>
                    <RichTextEditor content={field.value || ''} onChange={field.onChange} placeholder="Key benefits..." />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="goals"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Goals & Objectives</FormLabel>
                  <FormControl>
                    <RichTextEditor content={field.value || ''} onChange={field.onChange} placeholder="Goals and objectives..." />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="border-t pt-6">
              <h3 className="text-lg font-semibold mb-4">Visuals</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {renderVisualUpload(1)}
                {renderVisualUpload(2)}
              </div>
            </div>
            <div className="border-t pt-6">
              <h3 className="text-lg font-semibold mb-4">Syllabus / Materials</h3>
              <div className="space-y-4">
                <PdfUpload
                  onUploadComplete={(url) => form.setValue('syllabus', url)}
                  currentUrl={form.watch('syllabus') || undefined}
                  label="Event syllabus or materials"
                  description="Upload PDF or provide URL"
                />
                <FormField
                  control={form.control}
                  name="syllabus"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Syllabus URL</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Or paste URL" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : 'Save Additional Information'}
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}
